import $api from "@/shared/http";
import { toApiError } from "@/shared/http/types";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "@/app/appStore";
import {
  DEALER_SETTINGS_MARKUPS_LIST_URL,
  DEALER_SETTINGS_MARKUPS_URL,
  DEALER_SETTINGS_QUANTITY_URL,
  DEALER_SETTINGS_VISIBILITY_URL,
} from "../../lib/constants";
import {
  buildDictionariesFromApi,
  buildSettingsFromApi,
} from "../../lib/mapDealerSettings";
import { cloneSettings } from "../../lib/cloneSettings";
import {
  StockPublicationDictionaries,
  StockPublicationSettings,
} from "../../types";
import {
  DealerMarkupsDto,
  DealerMarkupsFilterDto,
  DealerPublicationQuantityDto,
  DealerRemainsVisibilityDto,
} from "../types";

export type FetchStockPublicationPayload = {
  dictionaries: StockPublicationDictionaries;
  settings: StockPublicationSettings;
};

export type FetchStockPublicationArgs = {
  force?: boolean;
};

const extractErrorMessage = (error: unknown, fallback: string): string => {
  const apiError = toApiError(error);
  const detail =
    typeof apiError.data === "object" &&
    apiError.data !== null &&
    "detail" in apiError.data &&
    typeof (apiError.data as { detail: unknown }).detail === "string"
      ? (apiError.data as { detail: string }).detail
      : apiError.message;

  return detail || fallback;
};

export const fetchStockPublicationSettings = createAsyncThunk<
  FetchStockPublicationPayload,
  FetchStockPublicationArgs | void,
  { rejectValue: string; state: RootState }
>(
  "stockPublication/fetchStockPublicationSettings",
  async (_, { rejectWithValue }) => {
    try {
      const [visibilityRes, quantityRes, markupsRes, markupFiltersRes] =
        await Promise.all([
          $api.get<DealerRemainsVisibilityDto>(DEALER_SETTINGS_VISIBILITY_URL),
          $api.get<DealerPublicationQuantityDto>(DEALER_SETTINGS_QUANTITY_URL),
          $api.get<DealerMarkupsDto>(DEALER_SETTINGS_MARKUPS_URL),
          $api.get<DealerMarkupsFilterDto>(DEALER_SETTINGS_MARKUPS_LIST_URL),
        ]);

      const settings = buildSettingsFromApi(
        visibilityRes.data,
        quantityRes.data,
        markupsRes.data,
      );

      return {
        dictionaries: buildDictionariesFromApi(
          visibilityRes.data,
          markupFiltersRes.data,
        ),
        settings: cloneSettings(settings),
      };
    } catch (error) {
      return rejectWithValue(
        extractErrorMessage(error, "Не удалось загрузить настройки"),
      );
    }
  },
  {
    condition: (arg, { getState }) => {
      if (arg && arg.force) {
        return true;
      }
      const state = getState().stockPublication;
      return !(state.savedSettings && state.dictionaries);
    },
  },
);
